import React, { useEffect, useState } from 'react';
import { Modal, Tag, Input, Button, Select, message, Popover, Typography, Divider } from 'antd';
import { InfoCircleOutlined, FlagOutlined } from '@ant-design/icons';
import { CasoMejoraCU2, getCaso, transicionarCaso, comentarCaso, eliminarCaso, EstadoCaso } from '../../api/mejoraCU2Api';
import { ESTADO_LABEL, ESTADO_COLOR, ACCION_LABEL, transicionesDisponibles, Capacidades } from './estados';
import RegistroTransmisionModal from './RegistroTransmisionModal';
import RegistroTrasladoModal from './RegistroTrasladoModal';

const { TextArea } = Input;
const { Text } = Typography;

const PRIORIDAD_COLOR: Record<string, string> = { Alta: 'red', Media: 'blue', Baja: 'default' };
const ROL_LABEL: Record<string, string> = { FCT: 'Gestor', Smartup: 'Smartup' };
const ACCION_ELIMINAR = '__ELIMINAR__';

interface Props {
  casoId: number | null;
  capacidades: Capacidades;
  onClose: () => void;
  onChanged: () => void;
}

const CasoDetailModal: React.FC<Props> = ({ casoId, capacidades, onClose, onChanged }) => {
  const [caso, setCaso] = useState<CasoMejoraCU2 | null>(null);
  const [loading, setLoading] = useState(false);
  const [destino, setDestino] = useState<string | undefined>();
  const [tratamiento, setTratamiento] = useState('');
  const [mejora, setMejora] = useState('');
  const [nuevoComentario, setNuevoComentario] = useState('');
  const [saving, setSaving] = useState(false);
  const [registroOpen, setRegistroOpen] = useState(false);

  const cargar = async (id: number) => {
    setLoading(true);
    try {
      const data = await getCaso(id);
      setCaso(data);
      setTratamiento(data.tratamientoSmartup ?? '');
      setMejora(data.mejoraIncorporada ?? '');
      setDestino(undefined);
    } catch {
      message.error('No se pudo cargar el caso');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (casoId == null) return;
    let cancelado = false;
    (async () => {
      setLoading(true);
      try {
        const data = await getCaso(casoId);
        if (cancelado) return;
        setCaso(data);
        setTratamiento(data.tratamientoSmartup ?? '');
        setMejora(data.mejoraIncorporada ?? '');
        setDestino(undefined);
      } catch {
        if (!cancelado) message.error('No se pudo cargar el caso');
      } finally {
        if (!cancelado) setLoading(false);
      }
    })();
    return () => { cancelado = true; };
  }, [casoId]);

  if (casoId == null) return null;

  const disponibles = caso ? transicionesDisponibles(caso.estado, capacidades) : [];
  const transicionSel = disponibles.find(t => t.destino === destino);

  const confirmarEliminar = () => {
    if (!caso) return;
    Modal.confirm({
      title: '¿Eliminar este caso?',
      content: `Se eliminará el caso ${caso.codigo} y todos sus comentarios. Esta acción no se puede deshacer.`,
      okText: 'Eliminar',
      okButtonProps: { danger: true },
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await eliminarCaso(caso.id);
          message.success('Caso eliminado');
          onChanged();
          onClose();
        } catch (e: any) {
          message.error(e?.response?.data?.error ?? 'No se pudo eliminar el caso');
        }
      },
    });
  };

  const aplicar = async () => {
    if (!caso || !destino) return;
    if (destino === ACCION_ELIMINAR) { confirmarEliminar(); return; }
    if (!transicionSel) return;
    if (transicionSel.exigeTratamiento && !tratamiento.trim()) { message.warning('Indica el tratamiento aplicado'); return; }
    if (transicionSel.exigeMejora && !mejora.trim()) { message.warning('Indica la mejora incorporada'); return; }
    setSaving(true);
    try {
      await transicionarCaso(caso.id, {
        nuevoEstado: destino as EstadoCaso,
        tratamientoSmartup: tratamiento || undefined,
        mejoraIncorporada: mejora || undefined,
      });
      message.success('Caso actualizado');
      await cargar(caso.id);
      onChanged();
    } catch (e: any) {
      message.error(e?.response?.data?.error ?? 'No se pudo actualizar el caso');
    } finally { setSaving(false); }
  };

  const enviarComentario = async () => {
    if (!caso || !nuevoComentario.trim()) return;
    setSaving(true);
    try {
      await comentarCaso(caso.id, nuevoComentario);
      setNuevoComentario('');
      await cargar(caso.id);
    } catch { message.error('No se pudo añadir el comentario'); }
    finally { setSaving(false); }
  };

  // Cabecera compacta en una línea (código · título · estado · prioridad · info)
  const header = caso ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingRight: 28 }}>
      <Text strong style={{ whiteSpace: 'nowrap' }}>{caso.codigo}</Text>
      {(caso.entidadTipo === 'Transmision' || caso.entidadTipo === 'Traslado') ? (
        <a
          style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          title={`${caso.titulo} — ver ${caso.entidadTipo.toLowerCase()} vinculado`}
          onClick={() => setRegistroOpen(true)}
        >
          {caso.titulo}
        </a>
      ) : (
        <Text
          style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 400 }}
          title={caso.titulo}
        >
          {caso.titulo}
        </Text>
      )}
      <Tag color={ESTADO_COLOR[caso.estado]} style={{ marginInlineEnd: 0 }}>{ESTADO_LABEL[caso.estado]}</Tag>
      <Tag color={PRIORIDAD_COLOR[caso.prioridad]} icon={<FlagOutlined />} style={{ marginInlineEnd: 0 }}>{caso.prioridad}</Tag>
      <Popover
        trigger="click"
        placement="bottomRight"
        title="Información"
        content={
          <div style={{ maxWidth: 280, lineHeight: 1.9 }}>
            <div><Text type="secondary">Entidad: </Text>{caso.entidadTipo} #{caso.entidadRefId}</div>
            <div><Text type="secondary">Reconciliación: </Text>{caso.reconciliacionId ?? '—'}</div>
          </div>
        }
      >
        <InfoCircleOutlined style={{ cursor: 'pointer', color: '#1677ff', fontSize: 16 }} />
      </Popover>
    </div>
  ) : undefined;

  return (
    <>
      <Modal open={casoId != null} onCancel={onClose} footer={null} width={760} loading={loading} title={header}>
      {caso && (
        <>
          {/* Descripción */}
          <TextArea
            value={caso.descripcion}
            readOnly
            autoSize={{ minRows: 3 }}
            style={{ marginTop: 8, marginBottom: 16, background: '#fafafa' }}
          />

          {/* Resultado del tratamiento (si existe) */}
          {(caso.tratamientoSmartup || caso.mejoraIncorporada) && (
            <div style={{ marginBottom: 16, fontSize: 13 }}>
              {caso.tratamientoSmartup && (
                <div style={{ marginBottom: 4 }}><Text type="secondary">Tratamiento: </Text>{caso.tratamientoSmartup}</div>
              )}
              {caso.mejoraIncorporada && (
                <div><Text type="secondary">Mejora: </Text>{caso.mejoraIncorporada}</div>
              )}
            </div>
          )}

          {/* Acción (sin label; placeholder + Aplicar) */}
          {(disponibles.length > 0 || capacidades.gestionar) && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <Select
                  style={{ flex: 1 }}
                  placeholder="Selecciona una acción"
                  value={destino}
                  onChange={(v) => setDestino(v)}
                  options={[
                    ...disponibles.map(t => ({ value: t.destino as string, label: ACCION_LABEL[t.destino] })),
                    ...(capacidades.gestionar
                      ? [{ value: ACCION_ELIMINAR, label: <span style={{ color: '#cf1322' }}>Eliminar caso</span> }]
                      : []),
                  ]}
                />
                <Button
                  type="primary"
                  loading={saving}
                  onClick={aplicar}
                  disabled={
                    !destino
                    || (!!transicionSel?.exigeTratamiento && !tratamiento.trim())
                    || (!!transicionSel?.exigeMejora && !mejora.trim())
                  }
                >
                  Aplicar
                </Button>
              </div>
              {transicionSel?.exigeTratamiento && (
                <TextArea value={tratamiento} onChange={e => setTratamiento(e.target.value)} rows={3} placeholder="Tratamiento aplicado por Smartup" />
              )}
              {transicionSel?.exigeMejora && (
                <TextArea value={mejora} onChange={e => setMejora(e.target.value)} rows={3} placeholder="Mejora incorporada para evitar recurrencia" />
              )}
            </div>
          )}

          {/* Conversación en formato chat: Gestor (interno) izquierda, externo derecha */}
          <Divider orientation="left" style={{ margin: '8px 0 12px' }}>Conversación</Divider>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'flex-end' }}>
            <TextArea
              value={nuevoComentario}
              onChange={e => setNuevoComentario(e.target.value)}
              placeholder="Añadir comentario… (Enter envía · Mayús+Enter salto de línea)"
              autoSize={{ minRows: 1, maxRows: 6 }}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  enviarComentario();
                }
              }}
            />
            <Button onClick={enviarComentario} loading={saving}>Comentar</Button>
          </div>
          <div style={{ maxHeight: 384, overflowY: 'auto', padding: '0 4px' }}>
            {(caso.comentarios ?? []).length === 0 && <Text type="secondary">Sin comentarios todavía.</Text>}
            {(caso.comentarios ?? []).slice().reverse().map(c => {
              const esExterno = c.autorTipo === 'Smartup';
              return (
                <div key={c.id} style={{ display: 'flex', justifyContent: esExterno ? 'flex-end' : 'flex-start', marginBottom: 8 }}>
                  <div
                    style={{
                      maxWidth: '78%',
                      background: esExterno ? '#e6f4ff' : '#f6ffed',
                      border: `1px solid ${esExterno ? '#91caff' : '#b7eb8f'}`,
                      borderRadius: 8,
                      padding: '6px 10px',
                    }}
                  >
                    <div style={{ fontSize: 11, marginBottom: 2, textAlign: esExterno ? 'right' : 'left' }}>
                      <Text strong>{c.autor}</Text>{' · '}
                      <Text type="secondary">{ROL_LABEL[c.autorTipo] ?? c.autorTipo}</Text>{' · '}
                      <Text type="secondary">{new Date(c.creadoEn).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</Text>
                    </div>
                    <div style={{ whiteSpace: 'pre-wrap' }}>{c.texto}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
      </Modal>
      {caso && caso.entidadTipo === 'Transmision' && (
        <RegistroTransmisionModal
          casoId={caso.id}
          titulo={caso.titulo}
          open={registroOpen}
          onClose={() => setRegistroOpen(false)}
        />
      )}
      {caso && caso.entidadTipo === 'Traslado' && (
        <RegistroTrasladoModal
          casoId={caso.id}
          titulo={caso.titulo}
          open={registroOpen}
          onClose={() => setRegistroOpen(false)}
        />
      )}
    </>
  );
};

export default CasoDetailModal;
